import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { LocalStorageState } from "./type";
import { debounce } from "@mui/material";
import { LocalStorageContext } from "./LocalStorageContext";

const DEBOUNCE_DELAY = 300;

export const LocalStorageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<LocalStorageState>({});

  const readValues = useCallback(() => {
    const data: Record<string, unknown> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const raw = localStorage.getItem(key);
        try {
          data[key] = raw ? JSON.parse(raw) : null;
        } catch {
          data[key] = raw;
        }
      }
    }
    setState(data);
  }, []);

  useEffect(() => {
    readValues();
  }, [readValues]);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (!e.key) {
        return;
      }
      setState((prev) => ({ ...prev, [e.key!]: e.newValue }));
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [readValues]);

  const get = useCallback((key: string) => state[key] ?? null, [state]);

  //with useCallback it will give warning so we are using useMemo
  const set = useMemo(
    () =>
      debounce((key: string, value: unknown) => {
        setState((prev) => ({ ...prev, [key]: value }));

        if (value === null || value === undefined) {
          localStorage.removeItem(key);
        } else {
          if (typeof value === "string") {
            localStorage.setItem(key, value);
          } else {
            try {
              localStorage.setItem(key, JSON.stringify(value));
            } catch {
              localStorage.setItem(key, String(value));
            }
          }
        }
      }, DEBOUNCE_DELAY),
    []
  );

  return (
    <LocalStorageContext.Provider value={{ state, get, set }}>
      {children}
    </LocalStorageContext.Provider>
  );
};
